import { useState, type FormEvent } from 'react';
import { domainInput, type DomainInput } from '@redirector/shared';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';
import { Field } from '../ui/Field.js';
import {
  issuesToFieldErrors,
  submitErrorToFieldErrors,
  type FieldErrors,
} from '../ui/formErrors.js';

export type DomainFormProps = {
  initial?: { hostname: string; fallbackUrl: string | null; enabled: boolean };
  submitLabel: string;
  onSubmit: (input: DomainInput) => Promise<void>;
  onCancel: () => void;
};

const INPUT =
  'w-full rounded-(--radius-md) border border-rule-strong bg-surface-raised px-(--space-3) py-(--space-2) text-ink';
const MONO = `${INPUT} font-mono text-[length:var(--text-small)]`;

export function DomainForm({ initial, submitLabel, onSubmit, onCancel }: DomainFormProps) {
  const [fields, setFields] = useState({
    hostname: initial?.hostname ?? '',
    fallbackUrl: initial?.fallbackUrl ?? '',
    enabled: initial?.enabled ?? true,
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const parsed = domainInput.safeParse(fields);
    if (!parsed.success) {
      setErrors(issuesToFieldErrors(parsed.error.issues));
      return;
    }

    setErrors({});
    setBusy(true);
    try {
      await onSubmit(parsed.data);
    } catch (error) {
      setErrors(submitErrorToFieldErrors(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-(--space-4)" noValidate>
      {errors.root !== undefined && <Callout tone="danger">{errors.root}</Callout>}

      <Field
        label="Hostname"
        error={errors.hostname}
        hint="The entry domain clients will point at us, for example client.example.com. No scheme, no path."
      >
        {({ id, describedBy, invalid }) => (
          <input
            id={id}
            autoFocus
            className={MONO}
            spellCheck={false}
            autoCapitalize="none"
            aria-describedby={describedBy}
            aria-invalid={invalid}
            value={fields.hostname}
            onChange={(event) => setFields((prev) => ({ ...prev, hostname: event.target.value }))}
          />
        )}
      </Field>

      <Field
        label="Fallback URL"
        error={errors.fallbackUrl}
        hint="Optional. Where traffic goes when a path on this domain is not configured. Falls back to the global setting when empty."
      >
        {({ id, describedBy, invalid }) => (
          <input
            id={id}
            className={MONO}
            spellCheck={false}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            value={fields.fallbackUrl}
            onChange={(event) => setFields((prev) => ({ ...prev, fallbackUrl: event.target.value }))}
          />
        )}
      </Field>

      <label className="flex items-center gap-(--space-2) text-[length:var(--text-small)]">
        <input
          type="checkbox"
          checked={fields.enabled}
          onChange={(event) => setFields((prev) => ({ ...prev, enabled: event.target.checked }))}
        />
        Enabled — when off, this domain stops matching and its traffic falls through
      </label>

      <div className="flex justify-end gap-(--space-2)">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </form>
  );
}
